import joi from 'joi';

import {
  type PathLookupAdvancedOptionsIcmp,
  type PathLookupAdvancedOptionsTcp,
  type PathLookupAdvancedOptionsUdp,
  pathLookupAdvancedOptionsSchema,
} from '../graphSettings/GraphAdvancedOptions';
import {
  type DeviceGroupAttribute,
  deviceGroupAttributes,
} from '../graphSettings/attributes';
import { getIpType } from '../ipUtils';

export interface GraphPathLookupParametersBase {
  groupBy: DeviceGroupAttribute;
  pathLookupType: 'unicast' | 'multicast' | 'hostToDefaultGW';
  type: 'pathLookup';
}

export interface AutomaticFirstHop {
  type: 'automatic';
  vrf?: string;
}

export interface EntryPoint {
  hostname: string;
  iface: string;
  sn: string;
}

export interface UserDefinedFirstHop {
  type: 'userDefined';
  entryPoints: EntryPoint[];
}

export type FirstHopAlgorithm = AutomaticFirstHop | UserDefinedFirstHop;

export function isAutomaticFirstHop(
  firstHop: FirstHopAlgorithm,
): firstHop is AutomaticFirstHop {
  return firstHop.type === 'automatic';
}

export function isUserDefinedFirstHop(
  firstHop: FirstHopAlgorithm,
): firstHop is UserDefinedFirstHop {
  return firstHop.type === 'userDefined';
}

export interface GraphPathLookupEndToEndParametersBase
  extends GraphPathLookupParametersBase {
  firstHopAlgorithm: FirstHopAlgorithm;
  securedPath: boolean;
}

export interface GraphPathLookupUnicastParametersBase
  extends GraphPathLookupEndToEndParametersBase {
  pathLookupType: 'unicast';
  destinationPoint: string;
  networkMode: boolean;
  startingPoint: string;
}

export type GraphPathLookupUnicastParametersTcp =
  GraphPathLookupUnicastParametersBase & PathLookupAdvancedOptionsTcp;
export type GraphPathLookupUnicastParametersUdp =
  GraphPathLookupUnicastParametersBase & PathLookupAdvancedOptionsUdp;
export type GraphPathLookupUnicastParametersIcmp =
  GraphPathLookupUnicastParametersBase & PathLookupAdvancedOptionsIcmp;
export type GraphPathLookupUnicastParameters =
  | GraphPathLookupUnicastParametersTcp
  | GraphPathLookupUnicastParametersUdp
  | GraphPathLookupUnicastParametersIcmp;

export interface GraphPathLookupMulticastParametersBase
  extends GraphPathLookupEndToEndParametersBase {
  pathLookupType: 'multicast';
  group: string;
  receiver?: string;
  source: string;
}

export type GraphPathLookupMulticastParametersTcp =
  GraphPathLookupMulticastParametersBase & PathLookupAdvancedOptionsTcp;
export type GraphPathLookupMulticastParametersUdp =
  GraphPathLookupMulticastParametersBase & PathLookupAdvancedOptionsUdp;
export type GraphPathLookupMulticastParametersIcmp =
  GraphPathLookupMulticastParametersBase & PathLookupAdvancedOptionsIcmp;
export type GraphPathLookupMulticastParameters =
  | GraphPathLookupMulticastParametersTcp
  | GraphPathLookupMulticastParametersUdp
  | GraphPathLookupMulticastParametersIcmp;

export interface GraphPathLookupHostToDefaultGWParameters
  extends GraphPathLookupParametersBase {
  pathLookupType: 'hostToDefaultGW';
  startingPoint: string;
  vrf?: string;
}

/**
 * This is the "pure input for path-lookup algorithm" (we can say it corresponds to
 * what user enters into a path-lookup form), purified from other information
 * that comes with path-lookup request as node positions, styling, snapshot,
 * overlay input (compare, intent check) etc.
 */
export type GraphPathLookupParameters =
  | GraphPathLookupUnicastParameters
  | GraphPathLookupMulticastParameters
  | GraphPathLookupHostToDefaultGWParameters;

export function isGraphPathLookupMulticastParameters(
  params?: GraphPathLookupParameters,
): params is GraphPathLookupMulticastParameters {
  return !!params && params.pathLookupType === 'multicast';
}

export function isGraphPathLookupHostToDefaultGWParameters(
  params?: GraphPathLookupParameters,
): params is GraphPathLookupHostToDefaultGWParameters {
  return !!params && params.pathLookupType === 'hostToDefaultGW';
}

export function isGraphPathLookupUnicastParametersTcp(
  value?: GraphPathLookupParameters | null,
): value is GraphPathLookupUnicastParametersTcp {
  return (
    !!value && value.pathLookupType === 'unicast' && value.protocol === 'tcp'
  );
}

export function isGraphPathLookupUnicastParametersUdp(
  value?: GraphPathLookupParameters | null,
): value is GraphPathLookupUnicastParametersUdp {
  return (
    !!value && value.pathLookupType === 'unicast' && value.protocol === 'udp'
  );
}

export function isGraphPathLookupUnicastParametersIcmp(
  value?: GraphPathLookupParameters | null,
): value is GraphPathLookupUnicastParametersIcmp {
  return (
    !!value &&
    value.pathLookupType === 'unicast' &&
    (value.protocol === 'icmp' || value.protocol === 'icmpv6')
  );
}

export function isGraphPathLookupMulticastParametersTcp(
  value?: GraphPathLookupParameters | null,
): value is GraphPathLookupMulticastParametersTcp {
  return (
    !!value && value.pathLookupType === 'multicast' && value.protocol === 'tcp'
  );
}

export function isGraphPathLookupMulticastParametersUdp(
  value?: GraphPathLookupParameters | null,
): value is GraphPathLookupMulticastParametersUdp {
  return (
    !!value && value.pathLookupType === 'multicast' && value.protocol === 'udp'
  );
}

export function isGraphPathLookupMulticastParametersIcmp(
  value?: GraphPathLookupParameters | null,
): value is GraphPathLookupMulticastParametersIcmp {
  return (
    !!value &&
    value.pathLookupType === 'multicast' &&
    (value.protocol === 'icmp' || value.protocol === 'icmpv6')
  );
}

// IP address / CIDR range
export const CidrMask = {
  max: {
    ip: 32,
    ipv6: 128,
  },
  networkMode: {
    ip: 8,
    ipv6: 120,
  },
  hostMode: {
    ip: 24,
    ipv6: 120,
  },
};

function getCidrMaskForIp(
  mode: keyof typeof CidrMask,
  ip?: string,
): number | null {
  if (!ip) {
    return null;
  }
  const ipType = getIpType(ip);
  return CidrMask[mode][ipType];
}

const ipRangeSchema = joi.string().ip({
  cidr: 'optional',
  version: ['ipv4', 'ipv6'],
});

export const isPathLookupMaskValid = (
  value: string | undefined,
  allowedMask: number | null,
): boolean => {
  if (!value) {
    return true;
  }
  const parts = value.split('/');
  if (parts.length === 1) {
    return true;
  }

  const maskPart = parts[1];
  if (maskPart === undefined) {
    return false;
  }

  const mask = Number.parseInt(maskPart, 10);
  if (Number.isNaN(mask) || mask < allowedMask!) {
    return false;
  }

  return true;
};

export const isMulticastZeroIp = (value: string): boolean =>
  getIpType(value) === 'ip' ? value === '0.0.0.0/0' : value === '::/0';

export const isUnicastHostModeValid = (
  startingPoint: string,
  destinationPoint: string,
): boolean => {
  const srcIpType = getIpType(startingPoint);
  const dstIpType = getIpType(destinationPoint);
  const srcMask =
    startingPoint.split('/')[1] || String(CidrMask.max[srcIpType]);
  const dstMask =
    destinationPoint.split('/')[1] || String(CidrMask.max[dstIpType]);
  const freeBits =
    CidrMask.max[srcIpType] +
    CidrMask.max[dstIpType] -
    (Number.parseInt(srcMask, 10) + Number.parseInt(dstMask, 10));

  return freeBits <= CidrMask.max[srcIpType] - CidrMask.hostMode[srcIpType];
};

const entryPointSchema = joi.object({
  hostname: joi.string().required(),
  iface: joi.string().required(),
  sn: joi.string().required(),
});

const entryPointsSchema = joi.array().items(entryPointSchema).min(1);

const firstHopAlgorithmSchema = joi.object({
  entryPoints: entryPointsSchema.when('type', {
    is: 'userDefined',
    then: joi.required(),
    otherwise: joi.forbidden(),
  }),
  type: joi.string().valid('automatic', 'userDefined').required(),
  vrf: joi.string().allow('').when('type', {
    is: 'automatic',
    then: joi.optional(),
    otherwise: joi.forbidden(),
  }),
});

// shared schema
const pathLookupParametersBaseSchema = joi.object().keys({
  groupBy: joi
    .string()
    .valid(...deviceGroupAttributes)
    .required(),
  type: joi.string().valid('pathLookup').required(),
});

const pathLookupEndToEndParametersSchema = pathLookupParametersBaseSchema
  .keys({
    firstHopAlgorithm: firstHopAlgorithmSchema.required(),
    securedPath: joi.boolean().default(false),
  })
  .concat(pathLookupAdvancedOptionsSchema);

function getCidrErrorType(ip: string | undefined): string {
  if (!ip) {
    return 'ip.cidr';
  }
  const ipType = getIpType(ip) === 'ip';
  return ipType ? 'ipv4.cidr' : 'ipv6.cidr';
}

// unicast schema covers GraphPathLookupUnicastRequestTcp, GraphPathLookupUnicastRequestUdp, GraphPathLookupUnicastRequestIcmp
export const pathLookupUnicastParametersSchema =
  pathLookupEndToEndParametersSchema
    .keys({
      destinationPoint: ipRangeSchema.required(),
      networkMode: joi.boolean().required(),
      pathLookupType: joi.string().valid('unicast').required(),
      startingPoint: ipRangeSchema.required(),
    })
    .custom((value, helpers) => {
      const { destinationPoint, networkMode, startingPoint, protocol } = value;
      const srcIpType = getIpType(startingPoint);
      const dstIpType = getIpType(destinationPoint);

      if (srcIpType !== dstIpType) {
        return helpers.error('Source and destination IP version mismatch');
      }

      if (protocol === 'icmp') {
        if (srcIpType !== 'ip') {
          return helpers.error('ICMP only over ipv4');
        }
      }

      if (protocol === 'icmpv6') {
        const srcIpType = getIpType(startingPoint);
        if (srcIpType !== 'ipv6') {
          return helpers.error('ICMPv6 only over ipv6');
        }
      }

      if (networkMode) {
        if (
          !isPathLookupMaskValid(
            startingPoint,
            getCidrMaskForIp('networkMode', startingPoint),
          )
        ) {
          return helpers.error(getCidrErrorType(startingPoint));
        }
        if (
          !isPathLookupMaskValid(
            destinationPoint,
            getCidrMaskForIp('networkMode', destinationPoint),
          )
        ) {
          return helpers.error(getCidrErrorType(destinationPoint));
        }
        // Also substitutes both masks validation
      } else if (!isUnicastHostModeValid(startingPoint, destinationPoint)) {
        return helpers.error('host mode over 255');
      }

      return value;
    });

// multicast schema covers GraphPathLookupMulticastRequestTcp, GraphPathLookupMulticastRequestUdp, GraphPathLookupMulticastRequestIcmp
export const pathLookupMulticastParametersSchema =
  pathLookupEndToEndParametersSchema
    .keys({
      group: ipRangeSchema.required(),
      pathLookupType: joi.string().valid('multicast').required(),
      receiver: ipRangeSchema,
      source: ipRangeSchema.required(),
    })
    .custom((value, helpers) => {
      const isValid =
        isPathLookupMaskValid(
          value.group,
          getCidrMaskForIp('networkMode', value.group),
        ) &&
        isPathLookupMaskValid(
          value.receiver,
          getCidrMaskForIp('networkMode', value.receiver ?? 'ip'),
        ) &&
        (isPathLookupMaskValid(
          value.source,
          getCidrMaskForIp('networkMode', value.source),
        ) ||
          isMulticastZeroIp(value.source));
      return isValid ? value : helpers.error(getCidrErrorType(value.group));
    });

export const pathLookupHostToDefaultGWParametersSchema =
  pathLookupParametersBaseSchema
    .keys({
      pathLookupType: joi.string().valid('hostToDefaultGW').required(),
      startingPoint: ipRangeSchema.required(),
      vrf: joi.string().allow(''),
    })
    .custom((value, helpers) =>
      isPathLookupMaskValid(
        value.startingPoint,
        getCidrMaskForIp('hostMode', value.startingPoint),
      )
        ? value
        : helpers.error(getCidrErrorType(value.startingPoint)),
    );

// Path lookup request data
export const graphPathLookupParametersSchema = joi.alternatives([
  pathLookupHostToDefaultGWParametersSchema,
  pathLookupMulticastParametersSchema,
  pathLookupUnicastParametersSchema,
]);
